import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "../API/Axios";

/**
 * The domain groups, fetched once and shared.
 *
 * The Team page builds a column per group and the Domain Mapping page edits
 * them, so both read from here rather than fetching their own copy. An admin
 * editing groups in another tab is picked up on window focus.
 *
 * This provider sits above the router, so it also mounts on the login screen.
 * Groups are admin chrome, not something an unauthenticated visitor can see,
 * so nothing is fetched until a token exists and a 401 is treated as "no
 * groups" rather than as a session problem to react to.
 */
const DomainGroupsContext = createContext(null);

const getAuthToken = () => {
    try {
        return localStorage.getItem("token");
    } catch {
        // Private mode or blocked storage — treat as unauthenticated.
        return null;
    }
};

export const useDomainGroups = () => {
    const context = useContext(DomainGroupsContext);

    if (!context) {
        throw new Error("useDomainGroups must be used within a DomainGroupsProvider");
    }

    const { loadIfNeeded } = context;

    // The provider never remounts, so a login that happens after it mounted
    // leaves it holding the empty list it started with. Every consumer renders
    // behind the authenticated routes, so the first one to mount is the signal
    // that a token is now in storage. loadIfNeeded() is a no-op after the first
    // load, so this costs one fetch however many consumers mount.
    useEffect(() => {
        loadIfNeeded();
    }, [loadIfNeeded]);

    return context;
};

export const DomainGroupsProvider = ({ children }) => {
    const [groups, setGroups] = useState([]);
    // Only claim to be loading if the mount effect is about to fetch.
    const [loading, setLoading] = useState(() => Boolean(getAuthToken()));
    const [error, setError] = useState(null);

    // Guards against a focus refetch landing while one is already in flight.
    const inFlight = useRef(false);
    // Set as soon as a fetch starts, so the automatic loads only ever run once.
    const attempted = useRef(false);
    // Set when the server rejects us, so no automatic load retries a request we
    // already know will 401. Only an explicit refresh() clears it.
    const unauthorized = useRef(false);

    const fetchGroups = useCallback(async () => {
        // No token: the login screen mounts this provider too, and fetching
        // there 401s. Skip silently — an unauthenticated visitor has no groups.
        if (!getAuthToken()) {
            setLoading(false);
            return;
        }

        if (inFlight.current) {
            return;
        }

        inFlight.current = true;
        attempted.current = true;
        setLoading(true);

        try {
            // Deliberately not API.getDomainGroups(): the shared CallApi error
            // handler answers any 401 by clearing storage and assigning
            // window.location = '/', which reloads the app, remounts this
            // provider and fetches again — the refresh loop. Going through the
            // axios instance keeps that handler out of the path while still
            // picking up the Authorization header from its request interceptor.
            const { data } = await axios.get("/domain-groups");

            if (data?.success) {
                setGroups(Array.isArray(data.data) ? data.data : []);
                setError(null);
            } else {
                setError(data?.message || "Failed to load domain groups");
            }
        } catch (err) {
            const status = err?.response?.status;

            if (status === 401 || status === 403) {
                // Not our call to make. Hold an empty list, no retry, no
                // redirect — whoever owns the session decides what happens next.
                unauthorized.current = true;
                setGroups([]);
                setError(null);
            } else {
                setError(err?.message || "Failed to load domain groups");
            }
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, []);

    /** The first load: provider mount, and the first consumer to mount after login. */
    const loadIfNeeded = useCallback(() => {
        if (attempted.current || unauthorized.current) {
            return;
        }

        fetchGroups();
    }, [fetchGroups]);

    /** Manual reload, from the Domain Mapping page. Used as an onClick handler, so it takes no arguments. */
    const refresh = useCallback(() => {
        unauthorized.current = false;
        return fetchGroups();
    }, [fetchGroups]);

    useEffect(() => {
        loadIfNeeded();
    }, [loadIfNeeded]);

    useEffect(() => {
        const onFocus = () => {
            if (unauthorized.current) {
                return;
            }

            fetchGroups();
        };

        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [fetchGroups]);

    const value = useMemo(() => {
        // Tier first, so a tier's columns stay together on the Team page now that
        // every tier has groups. Same order the backend snapshot uses.
        const visibleGroups = groups
            .filter((group) => group.visible !== false)
            .sort((a, b) => (
                String(a.tier).localeCompare(String(b.tier)) ||
                (a.order ?? 0) - (b.order ?? 0) ||
                a.name.localeCompare(b.name)
            ));

        return {
            groups,
            visibleGroups,
            /** The visible groups of one tier, in the order an admin put them in. */
            groupsForTier: (tier) => visibleGroups.filter((group) => group.tier === tier),
            loading,
            error,
            refresh,
            loadIfNeeded,
            setGroups
        };
    }, [groups, loading, error, refresh, loadIfNeeded]);

    return (
        <DomainGroupsContext.Provider value={value}>
            {children}
        </DomainGroupsContext.Provider>
    );
};

export default DomainGroupsContext;
